import { useEffect, useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Loader2, BookOpen, Video, Headphones, ClipboardList, CheckCircle2, AlertCircle, Cigarette, Smile } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '@/hooks/useAnalytics';
import { usePopupMessageContext } from '@/components/PopupMessageProvider';
import { ActivityViewerProps, ArticleData, VideoData, AudioData, AssessmentData } from './types';
import { useRef } from 'react';
import { ArticleContent } from './ArticleContent';
import { VideoContent } from './VideoContent';
import { AudioContent } from './AudioContent';
import { AssessmentContent } from './AssessmentContent';
import { CravingLogCard } from '@/components/actions/CravingLogCard';
import { CigaretteLogCard } from '@/components/actions/CigaretteLogCard';
import { MoodLogCard } from '@/components/actions/MoodLogCard';
import { toast } from 'sonner';
import { getArticleById, getVideoById, getAudioById, getAssessmentById } from '@/lib/api';

const typeIcons: Record<string, any> = {
  article: BookOpen,
  video: Video,
  audio: Headphones,
  assessment: ClipboardList,
  craving_log: AlertCircle,
  cigarette_log: Cigarette,
  mood_log: Smile,
};

export function ActivityViewer({ type, id, open, onOpenChange, onComplete }: ActivityViewerProps) {
  const { t } = useTranslation();
  const { trackArticleEngagement, trackVideoEngagement, trackAudioEngagement, trackButtonClick } = useAnalytics();
  const { checkTrigger } = usePopupMessageContext();
  const [mediaCompleted, setMediaCompleted] = useState(false);
  const audioTrackedRef = useRef(false);
  
  // Check if this is an action type
  const isActionType = type && ['craving_log', 'cigarette_log', 'mood_log'].includes(type);
  
  // Reset completion state when activity changes
  useEffect(() => {
    if (open) {
      setMediaCompleted(false);
      audioTrackedRef.current = false;
    }
  }, [open, id, type]);
  
  const typeLabels: Record<string, string> = {
    article: t('common:program.activityTypes.article', 'Article'),
    video: t('common:program.activityTypes.video', 'Video'),
    audio: t('common:program.activityTypes.audio', 'Audio'),
    assessment: t('common:program.activityTypes.assessment', 'Assessment'),
    craving_log: t('common:program.activityTypes.craving_log', 'Craving Log'),
    cigarette_log: t('common:program.activityTypes.cigarette_log', 'Cigarette Log'),
    mood_log: t('common:program.activityTypes.mood_log', 'Mood Log'),
  };

  const { data: article, isLoading: articleLoading } = useQuery({
    queryKey: ['activity-article', id],
    queryFn: async () => {
      const result = await getArticleById(id!);
      if (!result.success) throw new Error('error' in result ? result.error.message : t('common.index.failed_load_article', 'Failed to load article'));
      return result.data as ArticleData;
    },
    enabled: open && type === 'article' && !!id,
  });

  const { data: video, isLoading: videoLoading } = useQuery({
    queryKey: ['activity-video', id],
    queryFn: async () => {
      const result = await getVideoById(id!);
      if (!result.success) throw new Error('error' in result ? result.error.message : t('common.index.failed_load_video', 'Failed to load video'));
      return result.data as VideoData;
    },
    enabled: open && type === 'video' && !!id,
  });

  const { data: audio, isLoading: audioLoading } = useQuery({
    queryKey: ['activity-audio', id],
    queryFn: async () => {
      const result = await getAudioById(id!);
      if (!result.success) throw new Error('error' in result ? result.error.message : t('common.index.failed_load_audio', 'Failed to load audio'));
      return result.data as AudioData;
    },
    enabled: open && type === 'audio' && !!id,
  });

  const { data: assessment, isLoading: assessmentLoading } = useQuery({
    queryKey: ['activity-assessment', id],
    queryFn: async () => {
      const result = await getAssessmentById(id!);
      if (!result.success) throw new Error('error' in result ? result.error.message : t('common.index.failed_load_assessment', 'Failed to load assessment'));
      return result.data as AssessmentData;
    },
    enabled: open && type === 'assessment' && !!id,
  });

  // Track engagement when content loads
  useEffect(() => {
    if (article && type === 'article') {
      trackArticleEngagement('Opened', {
        article_id: article.id,
        article_title: article.title,
        article_category: article.category,
      });
    }
  }, [article]);

  useEffect(() => {
    if (video && type === 'video') {
      trackVideoEngagement('Video Opened', {
        video_id: video.id,
        video_title: video.title,
        is_vr: video.is_vr,
      });
    }
  }, [video]);

  useEffect(() => {
    if (assessment && type === 'assessment') {
      trackButtonClick('Assessment Opened', {
        assessment_id: assessment.id,
        assessment_title: assessment.title,
      });
    }
  }, [assessment]);

  // Track audio engagement
  useEffect(() => {
    if (audio && type === 'audio' && !audioTrackedRef.current) {
      audioTrackedRef.current = true;
      trackAudioEngagement('Opened', {
        audio_id: audio.id,
        audio_title: audio.title,
      });
    }
  }, [audio, type, trackAudioEngagement]);

  // Handle media completion (video/audio ended)
  const handleMediaEnded = useCallback((mediaType: 'video' | 'audio') => {
    setMediaCompleted(true);
    
    // Track completion based on media type
    if (mediaType === 'video' && video) {
      trackVideoEngagement('Video Completed', {
        video_id: video.id,
        video_title: video.title,
      });
    } else if (mediaType === 'audio' && audio) {
      trackAudioEngagement('Completed', {
        audio_id: audio.id,
        audio_title: audio.title,
      });
    }
    
    toast.success(t('common:activityViewer.completed', 'Activity completed!'), {
      icon: <CheckCircle2 className="w-5 h-5 text-green-500" />,
    });
    
    // Trigger activity completion popup
    checkTrigger('activity_completed', { count: 1 });
    
    onComplete?.();
  }, [onComplete, t, video, audio, trackVideoEngagement, trackAudioEngagement, checkTrigger]);

  // Handle manual completion (user clicks "Mark as Completed" button)
  const handleManualComplete = useCallback((mediaType: 'video' | 'audio') => {
    setMediaCompleted(true);
    
    // Track manual completion with flag
    if (mediaType === 'video' && video) {
      trackVideoEngagement('Video Manually Completed', {
        video_id: video.id,
        video_title: video.title,
        manual: true,
      });
    } else if (mediaType === 'audio' && audio) {
      trackAudioEngagement('Manually Completed', {
        audio_id: audio.id,
        audio_title: audio.title,
        manual: true,
      });
    }
    
    toast.success(t('common:activityViewer.completed', 'Activity completed!'), {
      icon: <CheckCircle2 className="w-5 h-5 text-green-500" />,
    });
    
    // Trigger activity completion popup
    checkTrigger('activity_completed', { count: 1 });
    
    onComplete?.();
  }, [onComplete, t, video, audio, trackVideoEngagement, trackAudioEngagement, checkTrigger]);

  // Handle action completion (craving/cigarette/mood logs)
  const handleActionComplete = useCallback(() => {
    onComplete?.();
    onOpenChange(false);
  }, [onComplete, onOpenChange]);

  if (!type || !id) return null;

  // For action types, render inline dialog content
  if (isActionType) {
    const Icon = typeIcons[type];
    const actionTitles: Record<string, string> = {
      craving_log: t('mindcotine:cravingActions.logCraving', 'Log Craving'),
      cigarette_log: t('mindcotine:cravingActions.logCigarettes', 'Log Cigarettes'),
      mood_log: t('mindcotine:cravingActions.logMood', 'Log Mood'),
    };

    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader className="space-y-3">
            <div className="flex items-center gap-2">
              {Icon && <Icon className="w-5 h-5 text-primary" />}
              <Badge variant="secondary">
                {typeLabels[type]}
              </Badge>
            </div>
            <DialogTitle>{actionTitles[type]}</DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {type === 'craving_log' && (
              <CravingLogCard onComplete={handleActionComplete} />
            )}
            {type === 'cigarette_log' && (
              <CigaretteLogCard onComplete={handleActionComplete} />
            )}
            {type === 'mood_log' && (
              <MoodLogCard onComplete={handleActionComplete} />
            )}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const isLoading = articleLoading || videoLoading || audioLoading || assessmentLoading;
  const Icon = typeIcons[type];
  
  const getTitle = () => {
    if (type === 'article') return article?.title;
    if (type === 'video') return video?.title;
    if (type === 'audio') return audio?.title;
    if (type === 'assessment') return assessment?.title;
    return '';
  };

  const getDescription = () => {
    if (type === 'article') return article?.excerpt;
    if (type === 'video') return video?.description;
    if (type === 'audio') return audio?.description;
    if (type === 'assessment') return assessment?.description;
    return '';
  };

  const handleClose = () => {
    onOpenChange(false);
    // Articles complete on close, video/audio require watching/listening to completion
    if (type === 'article') {
      onComplete?.();
    }
    // Note: video/audio only complete via onEnded event, assessment via submit
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="w-[95vw] sm:max-w-4xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <>
            <DialogHeader className="space-y-3">
              <div className="flex items-center gap-2">
                {Icon && <Icon className="w-5 h-5 text-primary" />}
                <Badge variant="secondary">
                  {typeLabels[type]}
                </Badge>
                {mediaCompleted && (type === 'video' || type === 'audio') && (
                  <Badge variant="default" className="bg-green-500 text-white">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    {t('common:activityViewer.completedBadge', 'Completed')}
                  </Badge>
                )}
              </div>
              <DialogTitle className="text-2xl sm:text-3xl font-bold pr-8">
                {getTitle()}
              </DialogTitle>
              {getDescription() && (
                <DialogDescription className="text-base">
                  {getDescription()}
                </DialogDescription>
              )}
            </DialogHeader>

            <div className="mt-6">
              {type === 'article' && article && (
                <ArticleContent article={article} />
              )}
              {type === 'video' && video && (
                <VideoContent 
                  video={video} 
                  onEnded={() => handleMediaEnded('video')}
                  onManualComplete={() => handleManualComplete('video')}
                  isCompleted={mediaCompleted}
                />
              )}
              {type === 'audio' && audio && (
                <AudioContent 
                  audio={audio} 
                  onEnded={() => handleMediaEnded('audio')}
                  onManualComplete={() => handleManualComplete('audio')}
                  isCompleted={mediaCompleted}
                />
              )}
              {type === 'assessment' && assessment && (
                <AssessmentContent 
                  assessment={assessment} 
                  onComplete={onComplete}
                  onClose={() => onOpenChange(false)}
                />
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export * from './types';
